import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { STATUS_META } from "@/lib/format";
import { Info, X } from "@phosphor-icons/react";

const PAGE_HINTS = {
  expenses: "Expenses are the qualified education costs your family pays. Add them here or let your beneficiary submit receipts. The app checks each one against IRS rules and your COA cap.",
  withdrawals: "Record every 529 distribution here \u2014 both qualified (education withdrawals, Roth rollovers, and student-loan repayments) and non-qualified. Recording all of them keeps your records tied to your 1099-Q. See the Help Center for how qualified and non-qualified distributions differ and are taxed.",
  // ONE RECEIPT PER PHOTO, SAID IN THE FIRST LINE. "The app reads each receipt"
  // never claimed a photo could hold several, but it was loose enough to be
  // read that way — sitting directly above the box that states the rule
  // outright (MyReceipts.jsx), the two sent mixed signals. Saying it here first
  // means the box below confirms it rather than correcting it. (ZEN-194,
  // following ZEN-191)
  //
  // "and import bank statements" went with it: this panel appears on ONE page,
  // the beneficiary's My Receipts, and there is nothing on it that imports a
  // bank statement — that is the account owner's Imports & Receipts screen.
  // It promised the reader something their page does not do.
  //
  // Every receipt screen, the bulk matcher included (BulkReceiptMatcher.jsx),
  // reads one receipt per photo through the same single upload (ZEN-185).
  receipts: "Photograph your receipts one at a time \u2014 one receipt per photo. The app reads each photo, fills in the details it can, and adds it to your list so you can check it before sending it to the account owner for approval.",
  approvals: `When your beneficiary submits an expense, it lands here. They send you the facts — what they paid, to whom, and the receipt. Approving adds it to your records and puts it under \u201c${STATUS_META.draft.label}\u201d on the Expenses page, where you say whether it is 529-eligible. Until you do, it counts toward nothing. You can also return it with a note.`,
};

const PAGE_ARTICLE = { withdrawals: "non-qualified-distributions" };

export function PageHelpPanel({ pageKey, hint: hintOverride }) {
  const qc = useQueryClient();
  const { data: prefs } = useQuery({ queryKey: ["user-prefs"], queryFn: async () => (await api.get("/user-prefs")).data });

  if (!prefs) return null;
  const dismissed = (prefs.dismissed_panels || []).includes(pageKey);
  if (dismissed) return null;
  const hint = hintOverride || PAGE_HINTS[pageKey];
  if (!hint) return null;

  const dismiss = async () => {
    const current = prefs.dismissed_panels || [];
    await api.put("/user-prefs", { dismissed_panels: [...current, pageKey] });
    qc.invalidateQueries({ queryKey: ["user-prefs"] });
  };

  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-[#034AA2]/20 bg-[#F4F7FB] px-4 py-3" data-testid={`page-help-${pageKey}`}>
      <Info size={18} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[#374151]">{hint}</p>
        <Link to={PAGE_ARTICLE[pageKey] ? `/help?article=${PAGE_ARTICLE[pageKey]}` : "/help"} className="mt-1 inline-block text-xs font-semibold text-[#034AA2] hover:underline">Learn more in the Help Center</Link>
      </div>
      <button onClick={dismiss} className="shrink-0 rounded p-0.5 text-[#6b7280] hover:bg-[#C2C9D1]/40" data-testid={`page-help-dismiss-${pageKey}`} aria-label="Dismiss">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
